import { deriveIteratorName, prettifyName } from 'app/services/formula/suggestionEngine';
import {
  DATE_OPERATOR_GLOSSES,
  DEFAULT_OPERATORS_BY_CATEGORY,
  OPERATOR_GLOSSES,
} from 'app/services/formula/suggestionEngine/suggestionEngine.constants';
import { getLiteralLabelOptions } from 'app/services/formula/valueLabels';
import _ from 'lodash';

import { LEX_TOKEN_TYPES, NUMERIC_TYPES, STRING_TYPES } from 'constants/parser';

import { ARRAY_TYPE_RE } from './visualBuilderModel.constants';

// Field option helpers (public)

/**
 * Resolve an entity type string through schemaParams.entities, following
 * string aliases with a cycle guard.  Returns the resolved entity object or null.
 *
 * The schema ships empty placeholder entries for primitive type names (e.g.
 * `entities.string === {}`). Those are NOT navigable entities — treating them
 * as one makes every string-typed field recurse into nothing and vanish from
 * the picker. An empty object therefore resolves to null (leaf), not an entity.
 *
 * @param {string} typeStr
 * @param {Object} entities
 * @param {Set} visited
 * @returns {Object|null}
 */
const resolveEntityObject = (typeStr, entities, visited) => {
  if (_.isNil(typeStr) || visited.has(typeStr)) {
    return null;
  }

  const entry = entities[typeStr];

  if (_.isPlainObject(entry)) {
    return _.isEmpty(entry) ? null : entry;
  }

  if (_.isString(entry) && !entry.startsWith('array')) {
    const next = new Set(visited);

    next.add(typeStr);

    return resolveEntityObject(entry, entities, next);
  }

  return null;
};

const MAX_FIELD_DEPTH = 4;

/**
 * Recursively walk entity fields to collect leaf field descriptors.
 *
 * @param {{ entityObj: Object, pathPrefix: string, labelPrefix: string, schemaParams: Object, visited: Set, depth: number, rootEntity: string, acc: Array }} params
 * @returns {void}
 */
const collectLeafFields = ({
  entityObj,
  pathPrefix,
  labelPrefix,
  schemaParams,
  visited,
  depth,
  rootEntity,
  acc,
}) => {
  if (depth > MAX_FIELD_DEPTH) {
    return;
  }

  const { entities, enumTypes } = schemaParams;

  Object.entries(entityObj).forEach(([key, typeStr]) => {
    const fieldPath = pathPrefix ? `${pathPrefix}.${key}` : key;
    const fieldLabel = labelPrefix ? `${labelPrefix} · ${prettifyName(key)}` : prettifyName(key);

    // Skip array-typed fields
    if (_.isString(typeStr) && typeStr.startsWith('array')) {
      return;
    }

    // Check if it's a nested entity
    const resolvedEntity = resolveEntityObject(typeStr, entities, new Set(visited));

    if (!_.isNil(resolvedEntity)) {
      if (visited.has(typeStr)) {
        return;
      }

      const next = new Set(visited);

      next.add(typeStr);
      collectLeafFields({
        entityObj: resolvedEntity,
        pathPrefix: fieldPath,
        labelPrefix: fieldLabel,
        schemaParams,
        visited: next,
        depth: depth + 1,
        rootEntity,
        acc,
      });

      return;
    }

    // Leaf field — include it
    const isEnum = !_.isNil(enumTypes) && !_.isNil(enumTypes[typeStr]);

    acc.push({
      path: fieldPath,
      label: fieldLabel,
      type: typeStr,
      isEnum,
      enumValues: isEnum ? enumTypes[typeStr] : null,
      valueLabelOptions: getLiteralLabelOptions(schemaParams.valueLabels, fieldPath),
      group: rootEntity,
    });
  });
};

/**
 * Get a flat list of all leaf fields accessible in the schema,
 * suitable for the comparison node's field picker.
 *
 * @param {{ schemaParams: Object }} params
 * @returns {Array<{ path: string, label: string, type: string, isEnum: boolean, enumValues: string[]|null, group: string }>}
 */
export const getFieldOptions = ({ schemaParams }) => {
  if (_.isNil(schemaParams?.entities) || _.isNil(schemaParams?.rootEntities)) {
    return [];
  }

  const { entities, rootEntities } = schemaParams;
  const acc = [];

  rootEntities.forEach(rootKey => {
    const rootEntry = entities[rootKey];
    const entityObj = _.isPlainObject(rootEntry)
      ? rootEntry
      : resolveEntityObject(rootEntry, entities, new Set());

    if (_.isNil(entityObj)) {
      return;
    }

    collectLeafFields({
      entityObj,
      pathPrefix: rootKey,
      labelPrefix: prettifyName(rootKey),
      schemaParams,
      visited: new Set([rootKey]),
      depth: 1,
      rootEntity: rootKey,
      acc,
    });
  });

  return acc;
};

/**
 * Get array-typed fields from the schema root, for use as the arrayPath
 * selector in collection nodes.
 *
 * @param {{ schemaParams: Object }} params
 * @returns {Array<{ path: string, label: string, elementEntityName: string }>}
 */
export const getArrayFieldOptions = ({ schemaParams }) => {
  if (_.isNil(schemaParams?.entities) || _.isNil(schemaParams?.rootEntities)) {
    return [];
  }

  const { entities, rootEntities } = schemaParams;
  const acc = [];

  const walkForArrays = ({ entityObj, pathPrefix, labelPrefix, visited, depth }) => {
    if (depth > MAX_FIELD_DEPTH) {
      return;
    }

    Object.entries(entityObj).forEach(([key, typeStr]) => {
      const fieldPath = pathPrefix ? `${pathPrefix}.${key}` : key;
      const fieldLabel = labelPrefix ? `${labelPrefix} · ${prettifyName(key)}` : prettifyName(key);

      if (_.isString(typeStr) && typeStr.startsWith('array')) {
        const match = ARRAY_TYPE_RE.exec(typeStr);

        if (match) {
          acc.push({ path: fieldPath, label: fieldLabel, elementEntityName: match[1] });
        }

        return;
      }

      const resolvedEntity = resolveEntityObject(typeStr, entities, new Set(visited));

      if (!_.isNil(resolvedEntity) && !visited.has(typeStr)) {
        const next = new Set(visited);

        next.add(typeStr);
        walkForArrays({
          entityObj: resolvedEntity,
          pathPrefix: fieldPath,
          labelPrefix: fieldLabel,
          visited: next,
          depth: depth + 1,
        });
      }
    });
  };

  rootEntities.forEach(rootKey => {
    const rootEntry = entities[rootKey];
    const entityObj = _.isPlainObject(rootEntry)
      ? rootEntry
      : resolveEntityObject(rootEntry, entities, new Set());

    if (_.isNil(entityObj)) {
      return;
    }

    walkForArrays({
      entityObj,
      pathPrefix: rootKey,
      labelPrefix: prettifyName(rootKey),
      visited: new Set([rootKey]),
      depth: 1,
    });
  });

  return acc;
};

/**
 * Resolve the type string for a dot-separated field path within schemaParams.
 *
 * @param {string} arrayPath
 * @param {Object} schemaParams
 * @returns {string|null}
 */
export const resolveFieldTypeStr = (arrayPath, schemaParams) => {
  const { entities } = schemaParams;
  const segments = arrayPath.split('.');
  const [first, ...rest] = segments;
  const rootEntry = entities[first];

  const walkSegs = (entityObj, segs) => {
    if (!segs.length) {
      return null;
    }

    const [head, ...tail] = segs;
    const typeStr = entityObj[head];

    if (_.isNil(typeStr)) {
      return null;
    }

    if (!tail.length) {
      return typeStr;
    }

    const resolved = resolveEntityObject(typeStr, entities, new Set());

    if (_.isNil(resolved)) {
      return null;
    }

    return walkSegs(resolved, tail);
  };

  const rootEntityObj = _.isPlainObject(rootEntry)
    ? rootEntry
    : resolveEntityObject(rootEntry, entities, new Set());

  if (_.isNil(rootEntityObj)) {
    return null;
  }

  return walkSegs(rootEntityObj, rest);
};

/**
 * Get the leaf fields of the element entity for a given array path,
 * with paths prefixed by the derived iterator name.
 *
 * @param {{ schemaParams: Object, arrayPath: string }} params
 * @returns {Array<{ path: string, label: string, type: string, isEnum: boolean, enumValues: string[]|null }>}
 */
export const getElementFieldOptions = ({ schemaParams, arrayPath }) => {
  if (_.isNil(schemaParams?.entities) || !arrayPath) {
    return [];
  }

  const { entities, enumTypes } = schemaParams;

  const arrayTypeStr = resolveFieldTypeStr(arrayPath, schemaParams);

  if (!_.isString(arrayTypeStr)) {
    return [];
  }

  const match = ARRAY_TYPE_RE.exec(arrayTypeStr);

  if (!match) {
    return [];
  }

  const elementEntityName = match[1];
  const elementEntity = resolveEntityObject(elementEntityName, entities, new Set());

  if (!_.isPlainObject(elementEntity)) {
    return [];
  }

  const iteratorPrefix = deriveIteratorName(arrayPath) ?? '_item';
  const acc = [];

  Object.entries(elementEntity).forEach(([key, typeStr]) => {
    if (_.isString(typeStr) && typeStr.startsWith('array')) {
      return;
    }

    const resolvedEntity = resolveEntityObject(typeStr, entities, new Set());

    if (!_.isNil(resolvedEntity)) {
      return;
    }

    const isEnum = !_.isNil(enumTypes) && !_.isNil(enumTypes[typeStr]);
    const prettyEntityName = prettifyName(elementEntityName);

    acc.push({
      path: `${iteratorPrefix}.${key}`,
      label: prettifyName(key),
      type: typeStr,
      isEnum,
      enumValues: isEnum ? enumTypes[typeStr] : null,
      valueLabelOptions: getLiteralLabelOptions(
        schemaParams.valueLabels,
        `${iteratorPrefix}.${key}`,
      ),
      group: prettyEntityName,
    });
  });

  return acc;
};

// Operator options (public)

/**
 * Get the list of operator options valid for a given value type.
 *
 * @param {{ schemaParams: Object, valueType: string|null }} params
 * @returns {Array<{ value: string, gloss: string }>}
 */
export const getOperatorOptionsForType = ({ schemaParams, valueType }) => {
  const { commonTypes, enumTypes } = schemaParams ?? {};

  const getCategory = type => {
    if (_.isNil(type)) {
      return 'string';
    }

    if (NUMERIC_TYPES.includes(type)) {
      return 'numeric';
    }

    if (STRING_TYPES.includes(type)) {
      return 'string';
    }

    if (type === 'bool' || type === LEX_TOKEN_TYPES.bool) {
      return 'bool';
    }

    if (type === 'date' || type === LEX_TOKEN_TYPES.date) {
      return 'date';
    }

    if (!_.isNil(enumTypes) && !_.isNil(enumTypes[type])) {
      return 'enum';
    }

    return 'string';
  };

  const ops =
    commonTypes?.[valueType] ?? DEFAULT_OPERATORS_BY_CATEGORY[getCategory(valueType)] ?? [];

  const isDateCategory = getCategory(valueType) === 'date';

  return ops.map(op => ({
    value: op,
    gloss: (isDateCategory ? DATE_OPERATOR_GLOSSES[op] : null) ?? OPERATOR_GLOSSES[op] ?? op,
  }));
};
